import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generationConfigSelector } from '../../../store/editAiGeneration/editAiGeneration.selector';
import styled, { useTheme } from 'styled-components/native';
import {
  changeGenerationConfigTopic,
  removeGenerationConfigTopic,
  setGenerationConfigVoiceId,
  setGenerationConfigWithBgMusic,
  setGenerationConfigWithIntro,
} from '../../../store/editAiGeneration/editAiGeneration.slice';
import DeleteButton from '../../../components/shared/buttons/DeleteButton';
import Button from '../../../components/shared/buttons/Button';
import VoicesSlider from './VoiceSlider/VoicesSlider';
import { VOICES_LIST } from '../../../utils/voices.constants';

const GenerationConfigQuestionnaire = () => {
  const dispatch = useDispatch();
  const { topics, withBgMusic, withIntro, voiceId } = useSelector(generationConfigSelector);

  const handleChangeTopic = useCallback(
    (text: string, index: number) => {
      dispatch(changeGenerationConfigTopic({ index, value: text }));
    },
    [dispatch],
  );

  const handleRemoveTopic = useCallback(
    (index: number) => {
      dispatch(removeGenerationConfigTopic(index));
    },
    [dispatch],
  );

  const handleToggleWithIntro = useCallback(
    (value: boolean) => {
      dispatch(setGenerationConfigWithIntro(value));
    },
    [dispatch],
  );

  const handleToggleWithBgMusic = useCallback(
    (value: boolean) => {
      dispatch(setGenerationConfigWithBgMusic(value));
    },
    [dispatch],
  );

  const handleVoiceSelect = useCallback(
    (id: string) => {
      dispatch(setGenerationConfigVoiceId(id));
    },
    [dispatch],
  );

  return (
    <S.Container>
      <S.TopicInputsList>
        <S.TopicListTitle>What topics would you like to focus on:</S.TopicListTitle>
        {topics.map((topic, index) => (
          <Topic
            key={`topic-${index}`}
            withDelete={topics.length === index + 1}
            topic={topic}
            index={index}
            onChange={handleChangeTopic}
            onRemove={handleRemoveTopic}
          />
        ))}
      </S.TopicInputsList>
      <S.Toggle>
        <S.ToggleText>Do we want to add an intro to the video?</S.ToggleText>
        <ToggleButtons isActive={withIntro} onChange={handleToggleWithIntro} />
      </S.Toggle>

      <S.Toggle>
        <S.ToggleText>Do we want background music?</S.ToggleText>
        <ToggleButtons isActive={withBgMusic} onChange={handleToggleWithBgMusic} />
      </S.Toggle>

      <S.VoiceSliderContainer>
        <S.VoiceSliderTitle>Who will narrate your video?</S.VoiceSliderTitle>
        <VoicesSlider activeVoiceId={voiceId} voicesList={VOICES_LIST} onSelect={handleVoiceSelect} />
      </S.VoiceSliderContainer>
    </S.Container>
  );
};

const ToggleButtons = ({ isActive, onChange }) => {
  const handlePressYes = useCallback(() => {
    onChange(true);
  }, [onChange]);

  const handlePressNo = useCallback(() => {
    onChange(false);
  }, [onChange]);

  return (
    <S.ToggleButtons>
      <S.ToggleButton
        isActive={isActive}
        onPress={handlePressYes}
        content={<S.ButtonText isActive={isActive}>Yes</S.ButtonText>}
      />
      <S.ToggleButton
        isActive={!isActive}
        onPress={handlePressNo}
        content={<S.ButtonText isActive={!isActive}>No</S.ButtonText>}
      />
    </S.ToggleButtons>
  );
};

const Topic = ({ index, topic, onChange, onRemove, withDelete }) => {
  const theme = useTheme();
  const handleChange = useCallback(
    (e) => {
      onChange(e.target.value, index);
    },
    [index, onChange],
  );

  const handleRemove = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  return (
    <S.Topic>
      <S.TopicInput
        placeholder={'Write a topic here'}
        placeholderTextColor={theme.colors.gray19}
        value={topic}
        onChange={handleChange}
      />
      {withDelete ? (
        <S.EmptyView />
      ) : (
        <DeleteButton containerViewStyle={S.TopicRemoveButtonStyles} onPress={handleRemove} width={40} height={40} />
      )}
    </S.Topic>
  );
};

const S = {
  Container: styled.View`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    margin-top: 38px;
  `,
  TopicListTitle: styled.Text`
    color: ${({ theme }) => theme.colors.text};
    font-family: ${({ theme }) => theme.fontFamilies.Arimo};
    font-size: 21px;
    font-style: normal;
    font-weight: 400;
    line-height: 140%; /* 22.4px */
    margin-bottom: 14px;
  `,
  TopicInput: styled.TextInput`
    width: 100%;
    height: 40px;
    border: 1px solid ${({ theme }) => theme.colors.borderGray};
    border-radius: 14px;
    padding: 0 10px;
  `,
  TopicInputsList: styled.View`
    width: 100%;
  `,
  Topic: styled.View`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 10px;
  `,
  TopicRemoveButtonStyles: {
    marginLeft: 10,
  },
  EmptyView: styled.View`
    margin-left: 10px;
    width: 40px;
    height: 40px;
  `,
  Toggle: styled.View`
    display: flex;
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-top: 36px;
  `,
  ToggleText: styled.Text`
    color: ${({ theme }) => theme.colors.text};
    font-family: ${({ theme }) => theme.fontFamilies.Arimo};
    font-size: 21px;
    font-style: normal;
    font-weight: 400;
    line-height: 140%; /* 22.4px */
  `,
  ToggleButtons: styled.View`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
  `,
  ToggleButton: styled(Button)<{ isActive: boolean }>`
    width: 110px;
    height: 50px;
    margin: 0 5px;
    background-color: ${({ theme, isActive }) =>
      isActive ? theme.colors.toggleButtonActiveBackground : theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.primaryBlue};
    align-items: center;
    justify-content: center;
  `,
  ButtonText: styled.Text<{ isActive: boolean }>`
    text-align: center;
    color: ${({ theme, isActive }) => (isActive ? theme.colors.primaryBlue : theme.colors.text)};
    font-family: ${({ theme }) => theme.fontFamilies.Arimo};
    font-size: 16px;
    font-style: normal;
    font-weight: ${({ isActive }) => (isActive ? 700 : 400)};
    line-height: 140%; /* 22.4px */
  `,
  VoiceSliderContainer: styled.View`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 36px;
  `,
  VoiceSliderTitle: styled.Text`
    color: ${({ theme }) => theme.colors.text};
    font-family: ${({ theme }) => theme.fontFamilies.Arimo};
    font-size: 21px;
    font-style: normal;
    font-weight: 400;
    line-height: 140%; /* 22.4px */
    margin-bottom: 14px;
  `,
};

export default GenerationConfigQuestionnaire;
